// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase/config"; // Import correct

import { AuthProvider } from "./context/AuthContext";
import { BackupProvider } from "./context/BackupContext";
import Navbar from "./components/layout/Navbar";

// ✅ AJOUT : Services de notifications existants
import FCMService from "./services/FCMService";
import NotificationScheduler from "./services/NotificationScheduler";

// Public Pages
import Home from "./components/pages/Home";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import Contact from "./components/pages/Contact";

// Dashboard principal
import PrivateRoute from "./components/auth/PrivateRoute";
import RoleBasedDashboard from "./components/dashboard/RoleBasedDashboard";

// Super Admin Dashboard
import SuperAdminDashboard from "./components/dashboard/SuperAdminDashboard";
import DashboardReports from "./components/dashboard/DashboardReports";
import UserList from "./components/dashboard/UserList";
import CreateUser from "./components/dashboard/CreateUser";
import EditUser from "./components/dashboard/EditUser";
import DossiersList from "./components/dashboard/DossiersList";
import CreateDossier from "./components/dashboard/CreateDossier";
import EditDossier from "./components/dashboard/EditDossier";
import DossierDetails from "./components/dashboard/DossierDetails";
import AdminSettings from "./components/dashboard/AdminSettings";
import AdminCalendar from "./components/dashboard/AdminCalendar";
import AdminAddEvent from "./components/dashboard/AdminAddEvent";
import SubscriptionPlans from "./components/subscription/SubscriptionPlans";
import NotificationsOverview from "./components/dashboard/NotificationsOverview";
import NotificationCenter from "./components/dashboard/NotificationCenter";
import NotificationDiscussion from "./components/dashboard/NotificationDiscussion";
import DiscussionEquipe from "./components/dashboard/DiscussionEquipe";
import DossiersRetard from "./components/dashboard/DossiersRetard";
import DossiersClotures from "./components/dashboard/DossiersClotures";
import AdminDocuments from "./components/dashboard/AdminDocuments";
import DossierMessaging from "./components/dashboard/DossierMessaging";

// Composants de sauvegarde
import BackupDashboard from "./components/dashboard/BackupDashboard";
import BackupMonitor from "./components/dashboard/BackupMonitor";

// Client Dashboard
import ClientDashboard from "./components/dashboard/client/ClientDashboard";
import ClientDocuments from "./components/dashboard/client/ClientDocuments";
import ClientDiscussion from "./components/dashboard/client/ClientDiscussion";
import ClientNotificationDetail from "./components/dashboard/client/ClientNotificationDetail";
import ClientCalendar from "./components/dashboard/client/ClientCalendar";
import DossierAgenda from "./components/dashboard/client/DossierAgenda";
import DossierMessagerie from "./components/dashboard/client/DossierMessagerie";
import ClientNotifications from "./components/dashboard/client/ClientNotifications";

// AJOUT : Import MultiDashboardClient pour la sélection multi-projets
import MultiDashboardClient from "./components/dashboard/client/MultiDashboardClient";

// Hyperadmin
import HyperadminPanel from "./components/hyperadmin/HyperadminPanel";

// Client Admin
import SuperClientDashboard from "./components/dashboard/SuperClientDashboard";
import ClientSettings from "./components/clientadmin/pages/ClientSettings";

// AJOUT : Import UnifiedMessageModal pour la synchronisation
import UnifiedMessageModal from './components/shared/UnifiedMessageModal';

// EmailJS
import emailjs from 'emailjs-com';

const EMAIL_USER_ID = "Hd2Xc78S4GD3ldisMexnG";

// NOUVEAU : Wrapper pour utiliser UnifiedMessageModal avec la collection discussions
const AdminDiscussionWrapper = () => {
  const { dossierId, discussionId } = useParams();
  const [discussionData, setDiscussionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (discussionId) {
      const loadDiscussion = async () => {
        try {
          console.log("🔍 Chargement discussion ID:", discussionId);
          
          // Charger depuis la collection "discussions"
          const docRef = doc(db, 'discussions', discussionId);
          const docSnap = await getDoc(docRef);
          
          console.log("🔍 Document existe:", docSnap.exists());
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            console.log("🔍 Données chargées:", data);
            
            setDiscussionData({ 
              id: docSnap.id, 
              ...data,
              // Adapter les données pour UnifiedMessageModal
              title: data.dossierTitre || `Discussion - ${dossierId}`,
              message: data.lastMessage || "Discussion en cours",
              replies: data.messages || [], // Les messages sont dans "messages"
              createdAt: data.createdAt,
              createdBy: data.participants?.[0] || 'system'
            });
          } else {
            console.error("❌ Document discussion non trouvé:", discussionId);
          }
        } catch (error) {
          console.error('❌ Erreur chargement discussion:', error);
        } finally {
          setLoading(false);
        }
      };
      loadDiscussion();
    }
  }, [discussionId, dossierId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3">Chargement de la discussion...</span>
      </div>
    );
  }
  
  if (!discussionData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">Discussion non trouvée</h2>
          <p className="text-gray-500 mt-2">Cette discussion n'existe pas ou vous n'y avez pas accès.</p>
          <button 
            onClick={() => window.history.back()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  console.log('🎯 AdminDiscussionWrapper: Passage de discussionId:', discussionId);

  return (
    <UnifiedMessageModal
      isOpen={true}
      onClose={() => window.history.back()}
      discussionData={discussionData}
      discussionId={discussionId} // ✅ CORRIGÉ: La prop manquante !
      isAdmin={true}
      customCollection="discussions"
    />
  );
};

function App() {
  // ✅ AJOUT : Initialisation des services de notifications existants
  useEffect(() => {
    const initializeServices = async () => {
      try {
        console.log('🚀 APP: Initialisation des services de notifications...');
        
        // Initialiser FCM Service
        await FCMService.initialize();
        
        // Démarrer le scheduler de notifications
        NotificationScheduler.start();
        
        console.log('✅ APP: Services de notifications initialisés');
      } catch (error) {
        console.error('❌ APP: Erreur initialisation services:', error);
      }
    };

    initializeServices();

    // Cleanup au démontage
    return () => {
      NotificationScheduler.stop();
    };
  }, []);

  // Initialisation EmailJS au démarrage
  useEffect(() => {
    try {
      emailjs.init(EMAIL_USER_ID);
      console.log("✅ EmailJS initialisé");
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation d'EmailJS:", error);
    }
  }, []);

  // MODIFIÉ : Écouteur pour ouvrir les discussions depuis les notifications
  useEffect(() => {
    const handleOpenDiscussion = (event) => {
      const { dossierId, discussionId } = event.detail;
      console.log('Ouverture discussion pour dossier:', dossierId, 'discussion:', discussionId);
      
      // Rediriger vers la route de messagerie du dossier avec l'ID de discussion
      window.location.href = `/admin/dossier/${dossierId}/messagerie/${discussionId}`;
    };

    window.addEventListener('openDiscussion', handleOpenDiscussion);
    
    return () => {
      window.removeEventListener('openDiscussion', handleOpenDiscussion);
    };
  }, []);

  return (
    <AuthProvider>
      <BackupProvider>
        <Router>
          <Navbar />
          <main className="container mx-auto px-4 py-8">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={
                <PrivateRoute>
                  <RoleBasedDashboard />
                </PrivateRoute>
              } />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/contact" element={<Contact />} />

              {/* Route directe pour les paramètres client - SOLUTION SIMPLE */}
              <Route
                path="/admin-clients/:clientId/preferences"
                element={
                  <PrivateRoute>
                    <ClientSettings />
                  </PrivateRoute>
                }
              />

              {/* Routes spécifiques client - ORDRE IMPORTANT : routes spécifiques AVANT la route générale */}
              <Route
                path="/clients/:dossierId/preferences/*"
                element={
                  <PrivateRoute>
                    <SuperClientDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clients/:dossierId/notification/:notificationId"
                element={
                  <PrivateRoute>
                    <ClientNotificationDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clients/:dossierId/discussion/:discussionId"
                element={
                  <PrivateRoute>
                    <ClientDiscussion />
                  </PrivateRoute>
                }
              />

              {/* Client Admin Router - APRÈS les routes spécifiques */}
              <Route
                path="/admin-clients/*"
                element={
                  <PrivateRoute>
                    <SuperClientDashboard />
                  </PrivateRoute>
                }
              />

              {/* Role-Based Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <RoleBasedDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <PrivateRoute>
                    <SubscriptionPlans />
                  </PrivateRoute>
                }
              />

              {/* Super Admin Routes */}
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <SuperAdminDashboard />
                  </PrivateRoute>
                }
              >
                <Route index element={<DashboardReports />} />
                <Route path="users" element={<UserList />} />
                <Route path="create-user" element={<CreateUser />} />
                <Route path="edit-user/:id" element={<EditUser />} />
                <Route path="dossiers" element={<DossiersList />} />
                <Route path="create-dossier" element={<CreateDossier />} />
                <Route path="nouveau-dossier" element={<CreateDossier />} />
                <Route path="edit-dossier/:id" element={<EditDossier />} />
                <Route path="dossiers-retard" element={<DossiersRetard />} />
                <Route path="dossiers-clotures" element={<DossiersClotures />} />
                <Route path="dossier/:id" element={<DossierDetails />} />
                
                {/* AJOUT : Routes pour les discussions avec synchronisation */}
                <Route
                  path="dossier/:dossierId/messagerie/:discussionId"
                  element={<AdminDiscussionWrapper />}
                />
                <Route
                  path="dossier/:dossierId/messaging/:discussionId"
                  element={<AdminDiscussionWrapper />}
                />
                {/* Garde l'ancienne route pour compatibilité */}
                <Route
                  path="dossier/:dossierId/messagerie"
                  element={<DossierMessaging />}
                />
                
                <Route path="settings" element={<AdminSettings />} />
                <Route path="calendar" element={<AdminCalendar />} />
                <Route path="calendar/add" element={<AdminAddEvent />} />
                <Route path="notifications" element={<NotificationsOverview />} />
                <Route path="messagerie" element={<NotificationsOverview />} />
                <Route path="messagerie/archivees" element={<NotificationsOverview />} />
                <Route path="notifications/nouvelle" element={<NotificationCenter />} />
                <Route path="notifications/:id" element={<NotificationDiscussion />} />
                <Route path="discussion-equipe" element={<DiscussionEquipe />} />
                <Route path="dossiers/:dossierId/documents" element={<AdminDocuments />} />
                
                {/* Routes de sauvegarde */}
                <Route path="sauvegarde" element={<BackupDashboard />} />
                <Route path="sauvegarde/monitoring" element={<BackupMonitor />} />
              </Route>

              {/* NOUVEAU : Route pour sélection multi-projets - DOIT ÊTRE AVANT /client/:dossierId */}
              <Route
                path="/client"
                element={
                  <PrivateRoute>
                    <MultiDashboardClient />
                  </PrivateRoute>
                }
              />

              {/* Client Dashboard Principal - GARDÉ IDENTIQUE */}
              <Route
                path="/client/:dossierId"
                element={
                  <PrivateRoute>
                    <ClientDashboard />
                  </PrivateRoute>
                }
              >
                <Route index element={<ClientCalendar />} />
                <Route path="agenda" element={<DossierAgenda />} />
                <Route path="messagerie" element={<DossierMessagerie />} />
                <Route path="notifications" element={<ClientNotifications />} />
                <Route path="notifications/:id" element={<NotificationDiscussion />} />
                <Route path="documents" element={<ClientDocuments />} />
              </Route>

              {/* Hyperadmin Panel */}
              <Route
                path="/hyperadmin/*"
                element={
                  <PrivateRoute>
                    <HyperadminPanel />
                  </PrivateRoute>
                }
              />

            </Routes>
          </main>
          
          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 5000,
              style: {
                background: '#333',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#10b981',
                },
              },
              error: {
                style: {
                  background: '#ef4444',
                },
              },
              loading: {
                style: {
                  background: '#3b82f6',
                },
              },
            }}
          />
        </Router>
      </BackupProvider>
    </AuthProvider>
  );
}

export default App;